<template>
    <van-popup v-model:show="show" position="bottom">
        <div class="flex flex-col p-[36px]">
            <div class="share-title">{{ $t('referral_link_title') }}</div>
            <div class="flex flex-col gap-[18px]">
                <div v-for="(item, index) in linkList" :key="index" class="link-item">
                    <div class="share-link">
                        <img src="@/assets/img/share/link.svg" alt="" class="w-[30px] h-[30px] mr-[4px]" />
                        <span class="share-text">
                            <span class="text=[28px] text-[#fff] mr-[4px]">Link {{ index + 1 }}</span>
                            {{ item }}
                        </span>
                    </div>
                    <div class="share-button" @click="handleCopy(item)">{{ $t('share_copy') }}</div>
                </div>
            </div>
            <div class="share-title">{{ $t('share_via_socials') }}</div>
            <div class="social-container">
                <div v-for="(item, index) in showOptions" :key="index" class="social-item" @click="hanldeShare(item)">
                    <img :src="item.icon" alt="" class="social-icon" />
                    <div class="social-text">{{ item.name }}</div>
                </div>
            </div>
        </div>
    </van-popup>
</template>
<script setup lang="ts">
import { useBaseStore } from '@/stores'

const show = defineModel<boolean>()
const emits = defineEmits(['shared'])
import { useClipboard1 } from '@/hooks/useCopy'
import { useI18n } from 'vue-i18n'
import FBIcon from '@/assets/img/share/logo-facebook.svg'
import Telegram from '@/assets/img/share/logo-telegram.svg'
import Viber from '@/assets/img/share/logo-viber.svg'
// import { H5Login } from '@/utils/H5Login'
import { encode } from 'js-base64'
import { getDeviceInfo, getPartner } from '@/utils'
import { getConfig } from '@/api/home'
import { ref, computed, watch, onMounted } from 'vue'
import { CUREENCY_PAGE_MAP } from '@/enums'

const props = defineProps({
    social: {
        type: Array,
        default: () => ['facebook', 'telegram', 'viber'],
    },
    config: {
        type: Object,
        default: () => ({}),
    },
    from: {
        type: String,
        default: () => '',
    },
})

const store = useBaseStore()
const { toClipboard } = useClipboard1()

const i18n = useI18n()

// 动态获取分享域名列表
const remoteDomains = ref<{ domain: string }[]>([])
const isLoadingDomains = ref(false)
const fetchShareDomains = async () => {
    if (isLoadingDomains.value) return

    isLoadingDomains.value = true
    try {
        const currency = store.wallet?.currency || ''
        const configName = `sharedomains.${getPartner()}.${currency}`
        const response = await getConfig(configName)
        if (response.code === 200 && response.data) {
            // 确保返回的数据是数组
            const domains = Array.isArray(response.data) ? response.data : [response.data]
            remoteDomains.value = domains
        }
    } catch (error) {
        console.error('get error:', error)
    } finally {
        isLoadingDomains.value = false
    }
}

// 监听货币变化，重新获取域名配置
watch(
    () => store.wallet?.currency,
    () => {
        if (store.wallet?.currency) {
            fetchShareDomains()
        }
    }
)

// show的时候拉一下
watch(show, (newVal) => {
    if (newVal) {
        fetchShareDomains()
    }
})

// + `/#${route.path}?id=${props.item.id}&pageNum=${props.pageNum}`
const options = computed(() => {
    return [
        {
            name: 'facebook',
            type: 'facebook',
            icon: FBIcon,
        },
        {
            name: 'telegram',
            type: 'telegram',
            icon: Telegram,
        },
        {
            name: 'viber',
            type: 'viber',
            icon: Viber,
        },
        // {
        //     name: i18n.t('Copy_invitation_code'),
        //     icon: 'weapp-qrcode',
        //     type: 'code',
        // },
    ]
})
const checkSocial = computed(() => props.social)
const showOptions = computed(() => options.value.filter((item) => checkSocial.value.includes(item.type)))

// 原有分享逻辑
const host = computed(() => {
    return import.meta.env.VITE_PWA_HOST
})

const shareUrl = computed(() => {
    return `${host.value}?userId=${store.userInfo.uid}&currency=${store.wallet?.currency}&from=${props.from}`
})

const hanldeShare = async (option) => {
    const shareOption = props.config
    if (!shareOption.url) {
        shareOption.url = shareUrl.value
    }
    if (option.type === 'facebook') {
        window.open(`https://m.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareOption.url)}`)
    } else if (option.type === 'telegram') {
        if (shareOption.url) {
            shareOption.url = `https://t.me/share/url?url=${shareOption.url}}`
        } else {
            const shareUrl = `${host.value}?` + `start=${encode('invite_' + store.tgInitData.id + '_tg')}==`
            shareOption.url = `https://t.me/share/url?url=${shareUrl}&text=${i18n.t('USDT_Plac_for_Share_Link_Telegram')}`
        }
        window.open(shareOption.url)
    } else if (option.type === 'viber') {
        window.open(`viber://forward?text=${encodeURIComponent(shareOption.url) || shareOption.text || ''} `)
    } else if (option.type === 'code') {
        toClipboard(`${store.userInfo.uid}`)
        showToast('copy code successful')
    } else {
        toClipboard(shareOption.url)
        showToast('copy link successful')
    }
    show.value = false
    emits('shared')
}

// 展示的分享链接列表
const linkList = computed(() => {
    // 如果动态域名还没加载完成，或者在US环境，或者货币不是PHP，则使用原有逻辑
    if (remoteDomains.value.length === 0) {
        return [shareUrl.value]
    }

    // 使用动态获取的域名列表
    return remoteDomains.value.map((item) => {
        return `https://${item.domain}/${CUREENCY_PAGE_MAP[store.wallet.currency] || 'app_ph.html'}?userId=${store.userInfo.uid}&currency=${
            store.wallet?.currency
        }&from=${props.from}`
    })
})

const handleCopy = (item: string) => {
    toClipboard(item)
    showToast('Copy successful')
    setTimeout(() => {
        show.value = false
    }, 500)
}
</script>
<style lang="scss" scoped>
.van-popup {
    border-radius: 30px 30px 0px 0px;
    background: #2f213b;
}

.share-title {
    color: #fff;
    font-size: 30px;
    font-weight: 700;
    margin: 46px 0px 24px 0px;
}

.share-title::before {
    content: '· ';
}

.link-item {
    @apply flex items-center justify-between;
    padding: 6px;
    padding-left: 12px;
    border-radius: 20px;
    background: #382b4a;
    height: 80px;
}

.share-link {
    @apply flex flex-1 items-center gap-[12px];
    margin: 12px;
    margin-right: 12px;
    color: #8e80a8;
    font-size: 24px;
    font-weight: 600;
    min-width: 0;

    .share-text {
        @apply w-full;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
}

.share-button {
    @apply flex items-center justify-center h-full;
    border-radius: 15px;
    background: linear-gradient(0deg, #fa3374 0%, #ff7aa5 100%);
    padding: 0 30px;
    height: 65px;
    flex-shrink: 0; /* 防止按钮被压缩 */
    color: #fff;
    font-size: 32px;
    font-weight: 600;
    transition: all 0.15s ease;

    &:active {
        transform: scale(0.95);
    }
}

.social-container {
    @apply flex items-center gap-[100px] justify-center my-[24px];

    .social-item {
        @apply flex flex-col items-center gap-[12px];
    }

    .social-icon {
        @apply w-[100px] h-[100px] rounded-full bg-[#282D40];
    }

    .social-text {
        color: #6b7889;
        text-align: center;
        font-size: 24px;
        font-weight: 400;
        text-transform: capitalize;
    }
}
</style>
